import { createFileRoute } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { Button } from '@/components/ui/button'
import { Download, Loader2, Plus } from 'lucide-react'
import { Label } from '@radix-ui/react-label'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Question,
  QuestionType,
  downloadQuiz,
  submitQuiz as submitQuizToServer,
} from '@/lib/Quiz'
import { guardQuizz } from '../lib/auth/routeGuards'
import { useToast } from '@/components/ui/use-toast'

interface GenerateQuizParams {
  inputText: string
  questionCount: number
  quizType: string
  language: string
}

export const Route = createFileRoute('/quizz')({
  beforeLoad: guardQuizz,
  component: QuizApp,
})

function QuizApp() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isGenerating, setIsGenerating] = useState(false)
  const [quiz, setQuiz] = useState<Question[]>([])
  const [score, setScore] = useState<{ score: number; total: number } | null>(
    null
  )
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, string[] | string>
  >({})
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  )
  const [editedQuestion, setEditedQuestion] = useState<Partial<Question>>({})
  const [isAddingQuestion, setIsAddingQuestion] = useState(false)
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    type: 'multiple-choice' as QuestionType,
    options: ['', '', '', ''],
    correctAnswer: [] as string[], // Initialize as empty array
  })

  const form = useForm({
    defaultValues: {
      questionCount: 5,
      quizType: 'multiple-choice',
      language: 'english',
      inputText: '',
    },
    onSubmit: async ({ value }) => {
      if (!value.inputText.trim()) {
        toast({
          title: t('routes.quizz.alerts.error-title', 'Error'),
          description: t('routes.quizz.alerts.empty-input'),
          variant: 'destructive',
        })
        return
      }

      setIsGenerating(true)
      setQuiz([])
      setScore(null)
      setSelectedAnswers({})

      try {
        const generatedQuiz = await generateQuizWithOpenAI(value)
        setQuiz(generatedQuiz)
      } catch (error) {
        // console.error('Quiz generation error:', error)
        toast({
          title: t('routes.quizz.alerts.error-title', 'Error'),
          description:
            error instanceof Error
              ? error.message
              : 'Failed to generate quiz. Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsGenerating(false)
      }
    },
  })

  async function generateQuizWithOpenAI({
    inputText,
    questionCount,
    quizType,
    language,
  }: GenerateQuizParams): Promise<Question[]> {
    // Get Azure OpenAI configuration from environment variables
    const AZURE_API_KEY = import.meta.env.VITE_AZURE_OPENAI_API_KEY
    const AZURE_API_VERSION = import.meta.env.VITE_AZURE_OPENAI_API_VERSION
    const AZURE_ENDPOINT = import.meta.env.VITE_AZURE_OPENAI_ENDPOINT

    if (!AZURE_API_KEY || !AZURE_API_VERSION || !AZURE_ENDPOINT) {
      throw new Error(
        'Azure OpenAI configuration missing. Please add VITE_AZURE_OPENAI_API_KEY, VITE_AZURE_OPENAI_API_VERSION, and VITE_AZURE_OPENAI_ENDPOINT to your environment variables.'
      )
    }
    const questionTypeForAI =
      quizType === 'short-answer' ? 'open-answer' : quizType
    
    // Build type-specific requirements
    let typeRequirements = ''
    let exampleFormat = ''
    
    if (questionTypeForAI === 'open-answer') {
      typeRequirements = `- ALL questions MUST be open-answer type (type: "open-answer")
- Each question should have "options": [] (empty array)
- Include a simple sample answer in "correctAnswer" field (as a string)`
      exampleFormat = `[
  {
    "id": "unique-id",
    "question": "Explain the concept of photosynthesis.",
    "options": [],
    "type": "open-answer",
    "correctAnswer": "Photosynthesis is the process by which plants convert light energy into chemical energy to produce glucose and oxygen."
  }
]`
    } else if (questionTypeForAI === 'multiple-choice') {
      typeRequirements = `- ALL questions MUST be multiple-choice type (type: "multiple-choice")
- Each question must have exactly 4 options
- Include the correct answer in the response`
      exampleFormat = `[
  {
    "id": "unique-id",
    "question": "What is the capital of France?",
    "options": ["Paris", "London", "Rome", "Berlin"],
    "correctAnswer": "Paris",
    "type": "multiple-choice"
  }
]`
    } else if (questionTypeForAI === 'true-false') {
      typeRequirements = `- ALL questions MUST be true-false type (type: "true-false")
- Each question must have exactly 2 options: ["True", "False"]
- Include the correct answer in the response`
      exampleFormat = `[
  {
    "id": "unique-id",
    "question": "The capital of France is Paris.",
    "options": ["True", "False"],
    "correctAnswer": "True",
    "type": "true-false"
  }
]`
    }
    
    // Map language to clearer instruction
    const languageInstruction = 
      language === 'arabic' 
        ? 'ARABIC - All questions, options, and answers MUST be in Arabic script. Use proper Arabic text and right-to-left formatting.'
        : language === 'french'
        ? 'FRENCH - All questions, options, and answers MUST be in French language.'
        : 'ENGLISH - All questions, options, and answers MUST be in English language.'
    
    const prompt = `
Generate a ${questionTypeForAI} quiz based on the following text. The response must be valid JSON.

LANGUAGE REQUIREMENT (CRITICAL):
${languageInstruction}
- The selected language is: ${language}
- ALL text in the response (questions, options, answers) MUST be in ${language === 'arabic' ? 'Arabic' : language === 'french' ? 'French' : 'English'}
- Do NOT use any other language

Text: ${inputText}

IMPORTANT REQUIREMENTS:
- Create exactly ${questionCount} questions
${typeRequirements}
- Response format must be a JSON array like this:
${exampleFormat}

CRITICAL: 
1. ALL questions must be of type "${questionTypeForAI}". Do NOT mix question types.
2. ALL text MUST be in ${language === 'arabic' ? 'Arabic' : language === 'french' ? 'French' : 'English'} language only.`

    try {
      // const response = await fetch(
      //   'https://api.openai.com/v1/chat/completions',
      //   {
      //     method: 'POST',
      //     headers: {
      //       'Content-Type': 'application/json',
      //       Authorization:
      //         'Bearer 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',
      //     },
      //     body: JSON.stringify({
      //       model: 'gpt-3.5-turbo',
      //       messages: [
      //         {
      //           role: 'system',
      //           content:
      //             'You are a quiz generator. Always respond with valid JSON in the specified format. For Arabic language, ensure proper Arabic script and right-to-left formatting.',
      //         },
      //         { role: 'user', content: prompt },
      //       ],
      //       temperature: 0.7,
      //     }),
      //   }
      // )
      // Azure OpenAI endpoint format: https://{resource-name}.openai.azure.com/openai/deployments/{deployment-name}/chat/completions?api-version={api-version}
      const azureEndpoint = `${AZURE_ENDPOINT.replace(/\/$/, '')}/openai/deployments/gpt-35-turbo/chat/completions?api-version=${AZURE_API_VERSION}`
      
      const response = await fetch(azureEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': AZURE_API_KEY,
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content:
                `You are a quiz generator. Always respond with valid JSON in the specified format. CRITICAL: You MUST respect the language specified in the user's request. If the user requests English, generate ONLY in English. If the user requests French, generate ONLY in French. If the user requests Arabic, generate ONLY in Arabic with proper Arabic script and right-to-left formatting. Never mix languages or use a different language than requested.`,
            },
            { role: 'user', content: prompt },
          ],
          temperature: 0.7,
          max_tokens: 1000,
        }),
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(
          error.error?.message || 'Failed to fetch OpenAI response'
        )
      }

      const result = await response.json()
      const quizContent = result.choices[0]?.message?.content

      if (!quizContent) {
        throw new Error('Empty response from OpenAI')
      }

      try {
        const parsedQuiz = JSON.parse(quizContent.trim()).map((q: any) => ({
          ...q,
          id: crypto.randomUUID(),
          type: q.type || (quizType === 'short-answer' ? 'open-answer' : 'multiple-choice'),
          options: q.options || [],
        }))
        if (!Array.isArray(parsedQuiz)) {
          throw new Error('Quiz response is not an array')
        }
        return parsedQuiz
      } catch (parseError) {
        // console.error('Parse error:', parseError)
        throw new Error('Failed to parse quiz content - invalid JSON format')
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error('Unknown error occurred')
    }
  }

  const handleAnswerSelect = (
    questionIndex: number,
    answers: string[] | string
  ) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionIndex]: answers,
    }))
  }

  const submitQuiz = () => {
    const unansweredQuestions = quiz.filter((q, index) => {
      if (q.type === 'open-answer') {
        const answer = selectedAnswers[index]
        return !answer || (typeof answer === 'string' && !answer.trim())
      } else {
        const answer = selectedAnswers[index]
        return !answer || (Array.isArray(answer) && answer.length === 0)
      }
    })

    if (unansweredQuestions.length > 0) {
      toast({
        title: t('routes.quizz.alerts.error-title', 'Error'),
        description: t('routes.quizz.alerts.answer-all'),
        variant: 'destructive',
      })
      return
    }

    const result = submitQuizToServer(quiz, selectedAnswers)
    setScore(result)
  }

  const handleEditQuestion = (question: Question) => {
    setEditingQuestionId(question.id)
    setEditedQuestion(question)
  }

  const saveEditedQuestion = () => {
    if (!editingQuestionId) return

    setQuiz((prevQuiz) =>
      prevQuiz.map((q) =>
        q.id === editingQuestionId
          ? {
              ...q,
              ...editedQuestion,
              options: editedQuestion.options || q.options,
              correctAnswer: editedQuestion.correctAnswer || q.correctAnswer,
            }
          : q
      )
    )

    setEditingQuestionId(null)
  }

  const cancelEditing = () => {
    setEditingQuestionId(null)
  }

  const handleAddQuestionClick = () => {
    setIsAddingQuestion(true)
    setNewQuestion({
      text: '',
      type: 'multiple-choice',
      options: ['', '', '', ''],
      correctAnswer: [],
    })
  }

  const saveNewQuestion = () => {
    if (!newQuestion.text.trim()) {
      toast({
        title: t('routes.quizz.alerts.error-title', 'Error'),
        description: t('routes.quizz.alerts.enter-question', 'Please enter a question'),
        variant: 'destructive',
      })
      return
    }

    if (
      newQuestion.type !== 'open-answer' &&
      (Array.isArray(newQuestion.correctAnswer)
        ? newQuestion.correctAnswer.length === 0
        : !newQuestion.correctAnswer)
    ) {
      toast({
        title: t('routes.quizz.alerts.error-title', 'Error'),
        description: t('routes.quizz.alerts.select-answer', 'Please select at least one correct answer'),
        variant: 'destructive',
      })
      return
    }

    const questionToAdd: Question = {
      id: crypto.randomUUID(),
      question: newQuestion.text,
      type: newQuestion.type,
      options:
        newQuestion.type === 'true-false'
          ? ['True', 'False']
          : newQuestion.type === 'multiple-choice'
            ? newQuestion.options.filter((opt) => opt.trim())
            : [],
      correctAnswer:
        newQuestion.type !== 'open-answer'
          ? newQuestion.correctAnswer
          : undefined,
    }

    setQuiz([...quiz, questionToAdd])
    setIsAddingQuestion(false)
  }

  const updateNewQuestionOption = (index: number, value: string) => {
    const newOptions = [...newQuestion.options]
    newOptions[index] = value
    setNewQuestion({ ...newQuestion, options: newOptions })
  }

  return (
    <div className="container mx-auto p-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-primary text-center mb-2">
          {t('routes.quizz.title')}
        </h1>
        <p className="text-gray-600 text-center italic mb-8">
          {t('routes.quizz.description')}
        </p>

        <div className="bg-white shadow-md rounded-lg p-6 mb-8">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('routes.quizz.form.language.label')}
                </Label>
                <form.Field
                  name="language"
                  children={(field) => (
                    <select
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="english">{t('routes.quizz.form.language.options.english')}</option>
                      <option value="french">{t('routes.quizz.form.language.options.french')}</option>
                      <option value="arabic">{t('routes.quizz.form.language.options.arabic')}</option>
                    </select>
                  )}
                />
              </div>

              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('routes.quizz.form.type.label')}
                </Label>
                <form.Field
                  name="quizType"
                  children={(field) => (
                    <select
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="multiple-choice">
                        {t('routes.quizz.form.type.options.multiple-choice')}
                      </option>
                      <option value="true-false">{t('routes.quizz.form.type.options.true-false')}</option>
                      <option value="short-answer">{t('routes.quizz.form.type.options.short-answer')}</option>
                    </select>
                  )}
                />
              </div>

              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('routes.quizz.form.question-count.label')}
                </Label>
                <form.Field
                  name="questionCount"
                  children={(field) => (
                    <Input
                      type="number"
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(parseInt(e.target.value))
                      }
                      min="1"
                      max="20"
                      className="w-full"
                    />
                  )}
                />
              </div>

              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('routes.quizz.form.input-text.label')}
                </Label>
                <form.Field
                  name="inputText"
                  children={(field) => (
                    <textarea
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      rows={5}
                      placeholder={t('routes.quizz.form.input-text.placeholder')}
                    />
                  )}
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={isGenerating}>
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('routes.quizz.form.button.generating')}
                    </>
                  ) : (
                    t('routes.quizz.form.button.generate')
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>

        {quiz.length > 0 && (
          <div className="bg-white shadow-md rounded-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">{t('routes.quizz.quiz-section.title')}</h2>
              <div className="flex gap-2">
                <Button
                  onClick={() =>
                    downloadQuiz(
                      quiz,
                      false,
                      form.state.values.language === 'arabic'
                        ? 'ar'
                        : form.state.values.language === 'french'
                          ? 'fr'
                          : 'en'
                    )
                  }
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {t('routes.quizz.quiz-section.download-button')}
                </Button>
                <Button
                  onClick={() =>
                    downloadQuiz(
                      quiz,
                      true,
                      form.state.values.language === 'arabic'
                        ? 'ar'
                        : form.state.values.language === 'french'
                          ? 'fr'
                          : 'en'
                    )
                  }
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {t('routes.quizz.quiz-section.download-with-answers')}
                </Button>
              </div>
            </div>
            {quiz.map((question, index) => (
              <div
                key={question.id}
                className="mb-6 p-4 border rounded-lg hover:bg-gray-50"
              >
                {editingQuestionId === question.id ? (
                  <div className="space-y-4">
                    <Input
                      value={editedQuestion.question}
                      onChange={(e) =>
                        setEditedQuestion({
                          ...editedQuestion,
                          question: e.target.value,
                        })
                      }
                      className="w-full"
                    />
                    <div className="space-y-2">
                      {(editedQuestion.options || []).map(
                        (option, optionIndex) => (
                          <div
                            key={optionIndex}
                            className="flex items-center space-x-2"
                          >
                            <Input
                              value={option}
                              onChange={(e) => {
                                const newOptions = [
                                  ...(editedQuestion.options || []),
                                ]
                                newOptions[optionIndex] = e.target.value
                                setEditedQuestion({
                                  ...editedQuestion,
                                  options: newOptions,
                                })
                              }}
                              className="flex-1"
                            />
                            <input
                              type="checkbox"
                              checked={
                                Array.isArray(
                                  editedQuestion.correctAnswer
                                )
                                  ? editedQuestion.correctAnswer.includes(
                                      option
                                    )
                                  : false
                              }
                              onChange={(e) => {
                                const currentAnswers = Array.isArray(
                                  editedQuestion.correctAnswer
                                )
                                  ? editedQuestion.correctAnswer
                                  : []

                                const updatedAnswers = e.target.checked
                                  ? [...currentAnswers, option]
                                  : currentAnswers.filter(
                                      (answer) => answer !== option
                                    )

                                setEditedQuestion({
                                  ...editedQuestion,
                                  correctAnswer: updatedAnswers,
                                })
                              }}
                              id={`edit-q${index}-option${optionIndex}`}
                            />
                            <Label
                              htmlFor={`edit-q${index}-option${optionIndex}`}
                            >
                              {t('routes.quizz.editor.correctLabel')}
                            </Label>
                          </div>
                        )
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={saveEditedQuestion}>{t('routes.quizz.editor.save')}</Button>
                      <Button variant="outline" onClick={cancelEditing}>
                        {t('routes.quizz.editor.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className="font-medium mb-2 cursor-pointer"
                      onClick={() => handleEditQuestion(question)}
                    >
                      {t('routes.quizz.quiz-section.question-prefix')} {index + 1}: {question.question}
                    </div>
                    {question.type === 'open-answer' ? (
                      <div className="mt-4">
                        <textarea
                          value={
                            typeof selectedAnswers[index] === 'string'
                              ? selectedAnswers[index]
                              : ''
                          }
                          onChange={(e) =>
                            handleAnswerSelect(index, e.target.value)
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none min-h-[100px]"
                          placeholder="Type your answer here..."
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(question.options || []).map(
                          (option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className={`flex items-center space-x-2 ${
                                Array.isArray(question.correctAnswer)
                                  ? question.correctAnswer.includes(option)
                                    ? 'text-green-600 font-medium'
                                    : ''
                                  : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  Array.isArray(selectedAnswers[index])
                                    ? selectedAnswers[index].includes(option)
                                    : false
                                }
                                onChange={(e) => {
                                  const currentAnswers = Array.isArray(
                                    selectedAnswers[index]
                                  )
                                    ? selectedAnswers[index]
                                    : []
                                  const updatedAnswers = e.target.checked
                                    ? [...currentAnswers, option]
                                    : currentAnswers.filter(
                                        (answer) => answer !== option
                                      )
                                  handleAnswerSelect(index, updatedAnswers)
                                }}
                                id={`q${index}-option${optionIndex}`}
                              />
                              <Label
                                htmlFor={`q${index}-option${optionIndex}`}
                              >
                                {option}
                                {(Array.isArray(question.correctAnswer)
                                  ? question.correctAnswer.includes(option)
                                  : false) && (
                                  <span className="ml-2 text-green-600">
                                    {t('routes.quizz.quiz-section.correct-answer-tag')}
                                  </span>
                                )}
                              </Label>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            <Button onClick={submitQuiz} className="w-full">
              {t('routes.quizz.quiz-section.submit-button')}
            </Button>
          </div>
        )}

        {!isAddingQuestion && (
          <Button
            onClick={handleAddQuestionClick}
            variant="outline"
            className="w-full mt-4 gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('routes.quizz.addQuestion.addCustomButton')}
          </Button>
        )}

        {isAddingQuestion && (
          <div className="bg-gray-50 p-4 rounded-lg mt-4">
            <h3 className="font-medium mb-3">{t('routes.quizz.addQuestion.title')}</h3>

            <div className="space-y-4">
              <div>
                <Label>{t('routes.quizz.addQuestion.fields.questionText')}</Label>
                <Input
                  value={newQuestion.text}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      text: e.target.value,
                    })
                  }
                  className="w-full mt-1"
                />
              </div>

              <div>
                <Label>{t('routes.quizz.addQuestion.fields.questionType')}</Label>
                <select
                  value={newQuestion.type}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      type: e.target.value as QuestionType,
                      options:
                        e.target.value === 'true-false'
                          ? ['True', 'False']
                          : ['', '', '', ''],
                      correctAnswer: [],
                    })
                  }
                  className="w-full px-3 py-2 border rounded-md mt-1"
                >
                  <option value="multiple-choice">{t('routes.quizz.addQuestion.fields.typeOptions.multiple-choice')}</option>
                  <option value="true-false">{t('routes.quizz.addQuestion.fields.typeOptions.true-false')}</option>
                  <option value="open-answer">{t('routes.quizz.addQuestion.fields.typeOptions.open-answer')}</option>
                </select>
              </div>

              {newQuestion.type === 'multiple-choice' && (
                <div className="space-y-2">
                  <Label>{t('routes.quizz.addQuestion.fields.optionsLabel')}</Label>
                  <div className="space-y-2">
                    {newQuestion.options.map((option, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2"
                      >
                        <Input
                          value={option}
                          onChange={(e) =>
                            updateNewQuestionOption(index, e.target.value)
                          }
                          className="flex-1"
                        />
                        <input
                          type="checkbox"
                          id={`new-option-${index}`}
                          checked={
                            Array.isArray(newQuestion.correctAnswer) &&
                            newQuestion.correctAnswer.includes(option)
                          }
                          onChange={(e) => {
                            const currentAnswers = Array.isArray(
                              newQuestion.correctAnswer
                            )
                              ? newQuestion.correctAnswer
                              : []

                            const updatedAnswers = e.target.checked
                              ? [...currentAnswers, option]
                              : currentAnswers.filter(
                                  (answer) => answer !== option
                                )

                            setNewQuestion({
                              ...newQuestion,
                              correctAnswer: updatedAnswers,
                            })
                          }}
                          disabled={!option.trim()}
                        />
                        <Label htmlFor={`new-option-${index}`}>
                          {t('routes.quizz.editor.correctLabel')}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {newQuestion.type === 'true-false' && (
                <RadioGroup
                  value={
                    Array.isArray(newQuestion.correctAnswer)
                      ? newQuestion.correctAnswer[0] || ''
                      : ''
                  }
                  onValueChange={(value) =>
                    setNewQuestion({
                      ...newQuestion,
                      correctAnswer: [value],
                    })
                  }
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="True" id="true-option" />
                    <Label htmlFor="true-option">{t('routes.quizz.addQuestion.fields.true')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="False" id="false-option" />
                    <Label htmlFor="false-option">{t('routes.quizz.addQuestion.fields.false')}</Label>
                  </div>
                </RadioGroup>
              )}

              <div className="flex space-x-2">
                <Button onClick={saveNewQuestion}>{t('routes.quizz.addQuestion.saveButton')}</Button>
                <Button
                  variant="outline"
                  onClick={() => setIsAddingQuestion(false)}
                >
                  {t('routes.quizz.addQuestion.cancelButton')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {score !== null && (
          <div className="bg-white shadow-md rounded-lg p-6 mb-8 text-center">
            <h2 className="text-2xl font-semibold mb-4">{t('routes.quizz.results.title')}</h2>
            <p className="text-lg">
              {t('routes.quizz.results.score-text')} {' '}
              <span className="font-bold text-primary">{score.score}</span> {' '}
              {t('routes.quizz.results.out-of')} {' '}
              <span className="font-bold text-primary">{score.total}</span> {' '}
              {t('routes.quizz.results.correct')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}